/**
 * A LAPFORRÁS — kép és PDF EGY felület mögött.
 *
 * A néző nem tudja, és nem is kell tudnia, hogy amit rajzol, egy fénykép
 * vagy egy PDF harmadik oldala: mindkettő lap, mérettel, és canvasra
 * rajzolható adott nagyításon és forgatással. Így a nagyítás, a forgatás,
 * a lapozás — és a #64 jelölői — EGYSZER épülnek meg, nem kétszer.
 *
 * ⚠️ A `pdfjs-dist`-ET CSAK A `pdfSource.ts` IMPORTÁLJA, és ő is csak
 * DINAMIKUSAN, innen. Ez nem méretoptimalizálás (bár az is): a pdf.js puszta
 * betöltése az AVX nélküli CI-gépen (Creatine) `SIGILL`-lel megöli a teszt-
 * folyamatot (#46). Egy statikus import bármelyik spec-fájl importláncában
 * — akár csak a `DocumentOriginal`-on át — a CI-n véletlenszerűen buktatná
 * az egész admin-suite-ot. A `viewer/imports.spec.ts` ezt lekötve tartja.
 */

import type { PageSize, Rotation } from './geometry';
import { displaySize } from './geometry';

/** Egy lap, ami rajzolásra kész. */
export interface SourcePage {
  /** A dokumentum SAJÁT álló helyzetében, 1-es nagyításon. */
  size: PageSize;
  /**
   * Kirajzolja a lapot a canvasra. A canvas méretét is ő állítja be —
   * a nagyítás, a forgatás és a képernyő pixelsűrűsége együtt dönti el.
   */
  render(canvas: HTMLCanvasElement, scale: number, rotation: Rotation): RenderTask;
}

/** Egy futó rajzolás. Megszakítható: lapozáskor a félkész rajz ne írja felül az újat. */
export interface RenderTask {
  promise: Promise<void>;
  cancel(): void;
}

export interface PageSource {
  pageCount: number;
  /** Az oldal 1-től számol, mint a PDF-ben és a jelölő `page` mezőjében. */
  page(pageNumber: number): Promise<SourcePage>;
  destroy(): void;
}

export type SourceKind = 'image' | 'pdf';

export async function openSource(blob: Blob, kind: SourceKind): Promise<PageSource> {
  if (kind === 'pdf') {
    const { openPdf } = await import('./pdfSource');
    return openPdf(await blob.arrayBuffer());
  }
  return openImage(blob);
}

/**
 * A CANVAS FIZIKAI MÉRETE a képernyő pixelsűrűségével szorozva, a CSS-mérete
 * a logikai. Enélkül egy 2× sűrűségű kijelzőn a számla apró betűi elmosódnak
 * — és a könyvelő pont azokat olvassa.
 */
export function sizeCanvas(
  canvas: HTMLCanvasElement,
  page: PageSize,
  scale: number,
  rotation: Rotation
): number {
  const ratio = window.devicePixelRatio || 1;
  const shown = displaySize(page, scale, rotation);
  canvas.width = Math.max(1, Math.floor(shown.width * ratio));
  canvas.height = Math.max(1, Math.floor(shown.height * ratio));
  canvas.style.width = `${shown.width}px`;
  canvas.style.height = `${shown.height}px`;
  return ratio;
}

/**
 * A KÉP EGYOLDALAS FORRÁS. Az `imageOrientation: 'from-image'` az EXIF
 * szerinti állást veszi — a telefonnal fotózott számla így áll fel, ahogy a
 * telefon mutatta, és EZ a dokumentum saját álló helyzete, nem a nyers
 * szenzor-irány.
 */
async function openImage(blob: Blob): Promise<PageSource> {
  const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
  const size = { width: bitmap.width, height: bitmap.height };

  const page: SourcePage = {
    size,
    render(canvas, scale, rotation) {
      const ratio = sizeCanvas(canvas, size, scale, rotation);
      const context = canvas.getContext('2d');
      if (context !== null) {
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        const shown = displaySize(size, scale, rotation);
        // A KÖZÉPPONT KÖRÜL fordítunk: így a négy negyedfordulat ugyanazt a
        // dobozt tölti ki, nem csúszik ki a lap a canvasból.
        context.translate(shown.width / 2, shown.height / 2);
        context.rotate((rotation * Math.PI) / 180);
        context.drawImage(
          bitmap,
          (-size.width * scale) / 2,
          (-size.height * scale) / 2,
          size.width * scale,
          size.height * scale
        );
      }
      return { promise: Promise.resolve(), cancel: () => undefined };
    },
  };

  return {
    pageCount: 1,
    page: async () => page,
    destroy: () => bitmap.close(),
  };
}
